export interface RetryOptions {
  maxAttempts?: number
  baseDelayMs?: number
  signal?: AbortSignal
}

export type RetryResult<T> = { data: T; failed: false } | { data: null; failed: true }

export async function fetchWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<RetryResult<T>> {
  const { maxAttempts = 3, baseDelayMs = 1000, signal } = options
  let lastError: unknown

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (signal?.aborted) return { data: null, failed: true }
    try {
      const data = await fn()
      return { data, failed: false }
    } catch (error) {
      lastError = error
      if (signal?.aborted) return { data: null, failed: true }
      if (attempt < maxAttempts - 1) {
        const delay = baseDelayMs * Math.pow(2, attempt) * (0.5 + Math.random() * 0.5)
        await new Promise<void>((resolve) => {
          const onAbort = () => {
            clearTimeout(timer)
            resolve()
          }
          const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort)
            resolve()
          }, delay)
          signal?.addEventListener('abort', onAbort, { once: true })
        })
      }
    }
  }

  console.error(
    `fetchWithRetry failed after ${maxAttempts} attempt${maxAttempts > 1 ? 's' : ''}:`,
    lastError
  )
  return { data: null, failed: true }
}

export async function mapConcurrent<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let nextIndex = 0
  const errors: { index: number; error: unknown }[] = []
  const effectiveConcurrency = Math.max(1, Math.min(concurrency, items.length))

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++
      try {
        results[i] = await fn(items[i])
      } catch (error) {
        errors.push({ index: i, error })
      }
    }
  }

  await Promise.all(Array.from({ length: effectiveConcurrency }, worker))

  if (errors.length > 0) {
    throw new AggregateError(
      errors.map((e) => e.error),
      `mapConcurrent: ${errors.length}/${items.length} items failed (indices: ${errors.map((e) => e.index).join(', ')})`
    )
  }

  return results
}
